import { buffers, channel } from 'redux-saga';
import { call, put, select, spawn, take, takeLatest } from 'redux-saga/effects'
import { GET_GAME, SET_DECK } from './constants';
import {
  setActivePokemon,
  setBenches, setContextMenu,
  setDeckDone,
  setDeckPiles,
  setDiscardPiles,
  setGame,
  setGamePlayers,
  setGameTurn,
  setHands,
  setPrizePiles,
  setUsers,
  setVersion,
} from './actions';
import { selectActiveGame, selectPlayer, selectVersion } from './selectors';
import { get, setDeck } from '../services/game';
import { registerChannels } from '../services/pusher';
import { APP_DEBUG } from '../env';
import { MAIN } from './contextMenuTypes';
import { ensureCards } from '../Cards/sagas';
import { withCardDetails } from '../Cards/withCardDetails';
import { LANDED } from '../Coins/constants';
import { seen, toss } from '../Coins/actions';
import { selectSeenFlipVersion } from '../Coins/selectors';
import { tossFor } from '../Coins/tossFor';

/**
 * Every update of the game, from the player's own moves and from the opponent's, is applied here, one at a time and
 * in the order they came: a move that flipped coins waits for them to land (CoinToss), and an update that arrives
 * meanwhile waits its turn instead of showing first.
 */
const updates = channel(buffers.expanding());

export function* handleUpdateGame(gameReferences, { loading = false } = {}) {
  let finish;
  const applied = new Promise((resolve, reject) => {
    finish = (error) => (error ? reject(error) : resolve());
  });
  yield put(updates, { gameReferences, loading, finish });
  yield applied;
}

function* updateWorker() {
  while (true) {
    const { gameReferences, loading, finish } = yield take(updates);
    try {
      yield call(tossCoins, gameReferences, loading);
      yield call(applyUpdate, gameReferences);
      finish();
    } catch (error) {
      finish(error);
    }
  }
}

/**
 * A move's coins toss before the move shows. Opening a game shows it as it is: its last flips count as seen.
 */
function* tossCoins(game, loading) {
  const coinFlips = game.coin_flips;
  if (loading) {
    if (coinFlips) {
      yield put(seen(coinFlips.version));
    }
    return;
  }
  const activeGame = yield select(selectActiveGame);
  if (!activeGame || activeGame.get('id') !== game.id || activeGame.get('version') >= game.version) {
    return;
  }
  const player = yield select(selectPlayer);
  const coins = tossFor(game, { userId: player.getIn(['user', 'id']), seenVersion: yield select(selectSeenFlipVersion) });
  if (coins) {
    yield put(toss(coins.version, coins.flips, coins.waitForTap));
    yield take(LANDED);
  }
}

function* applyUpdate(gameReferences) {
  const cardsById = yield call(ensureCards);
  const initialGame = withCardDetails(gameReferences, cardsById);

  const activeGame = yield select(selectActiveGame);
  const version = yield select(selectVersion);

  const {
    activePokemon,
    benches,
    discardPiles,
    deckPiles,
    gameTurn,
    gamePlayers,
    hands,
    prizePiles,
    users,
    ...game
  } = initialGame;

  const handleUpdate = function* (entities, entityName, setEntitiesAction) {
    if (!activeGame || activeGame.get('id') !== game.id || version.get(entityName, 0) < game.version) {
      yield put(setVersion({ [entityName]: game.version }));
      yield put(setEntitiesAction(entities));
    } else if (APP_DEBUG) {
      console.log(`SKIPPED UPDATE: ${entityName}`);
    }
  };

  if (APP_DEBUG && activeGame) {
    console.log(`${(new Date()).toISOString()} RECEIVED UPDATE`, activeGame.get('version'), game.version);
  }

  if (activePokemon) {
    yield handleUpdate(activePokemon, 'activePokemon', setActivePokemon);
  }

  if (benches) {
    yield handleUpdate(benches, 'benches', setBenches);
  }

  if (deckPiles) {
    yield handleUpdate(deckPiles, 'deckPiles', setDeckPiles);
  }

  if (discardPiles) {
    yield handleUpdate(discardPiles, 'discardPiles', setDiscardPiles);
  }

  if (gamePlayers) {
    yield handleUpdate(gamePlayers, 'gamePlayers', setGamePlayers);
  }

  if (gameTurn) {
    yield handleUpdate(gameTurn, 'gameTurn', setGameTurn);
  }

  if (hands) {
    yield handleUpdate(hands, 'hands', setHands);
  }

  if (prizePiles) {
    yield handleUpdate(prizePiles, 'prizePiles', setPrizePiles);
  }

  if (users) {
    yield handleUpdate(users, 'users', setUsers);
  }

  yield put(setGame(game));
}

function* getGameTask(action) {
  const game = yield call(get, action.payload.get('id'));

  yield call(handleUpdateGame, game, { loading: true });

  const player = yield select(selectPlayer);

  const channelsSaga = yield call(registerChannels, game.id, player.getIn(['user', 'id']));

  yield put(setContextMenu(MAIN, player.getIn(['user', 'id'])));

  yield spawn(channelsSaga);
}

function* getGameWatcher() {
  yield takeLatest(GET_GAME, getGameTask);
}

function* setDeckTask(action) {
  const gameId = (yield select(selectActiveGame)).get('id');
  const game = yield call(setDeck, gameId, action.payload.get('deckId'));

  yield call(handleUpdateGame, game);

  yield put(setDeckDone());
}

function* setDeckWatcher() {
  yield takeLatest(SET_DECK, setDeckTask);
}

export default [
  updateWorker,
  setDeckWatcher,
  getGameWatcher,
];
